// Service Loader - Static service loading and instantiation
// ✅ NO DYNAMIC IMPORTS: modules are resolved from SERVICE_MODULE_MAP
// ✅ NO MAGIC STRINGS: Use typed enums and constants

import { validateInterfaceContract } from "./interfaceContractMap";
import { SERVICE_MODULE_MAP } from "./serviceModuleMap";
import { ServiceConfig, ServiceType, ServiceLoaderContract } from "./types";

export class ServiceLoader implements ServiceLoaderContract {
  /**
   * Load and instantiate a service based on configuration.
   * Module resolution is done via the static SERVICE_MODULE_MAP — no dynamic imports.
   */
  async loadService(
    config: ServiceConfig,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dependencies: Map<string, any>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<any> {
    try {
      // Resolve dependencies
      const resolvedDependencies = config.dependencies.map((depName) => {
        const dep = dependencies.get(depName);
        if (!dep && config.dependencies.length > 0) {
          throw new Error(
            `Service ${config.name} missing dependency: ${depName}`,
          );
        }
        return dep;
      });

      // Look up the module from the static map (fail-fast if not registered)
      const module = SERVICE_MODULE_MAP[config.module];
      if (!module) {
        throw new Error(
          `Service ${config.name}: module "${config.module}" not found in SERVICE_MODULE_MAP. ` +
            `Add a static import for this module to src/platform/services/serviceModuleMap.ts`,
        );
      }

      switch (config.type) {
        case ServiceType.SINGLETON:
          // For singleton, check if already exported instance (common pattern: export const instance = new Class())
          // Check for named export matching the class name (lowercase)
          const instanceName =
            config.class!.charAt(0).toLowerCase() + config.class!.slice(1);
          if (
            module[instanceName] &&
            typeof module[instanceName] === "object"
          ) {
            return module[instanceName];
          }
          // Check for default export that's already an instance
          if (
            module.default &&
            typeof module.default === "object" &&
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            !(module.default as any).prototype
          ) {
            return module.default;
          }
          // Otherwise, instantiate the class
          const SingletonClass = module[config.class!] || module.default;
          if (!SingletonClass) {
            throw new Error(
              `Service ${config.name}: Class ${config.class} not found in module`,
            );
          }
          // If it's already an instance (not a class), return it
          if (typeof SingletonClass !== "function") {
            return SingletonClass;
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return new (SingletonClass as any)(...resolvedDependencies);

        case ServiceType.INSTANCE:
          const InstanceClass = module[config.class!] || module.default;
          if (!InstanceClass) {
            throw new Error(
              `Service ${config.name}: Class ${config.class} not found in module`,
            );
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return new (InstanceClass as any)(...resolvedDependencies);

        case ServiceType.FACTORY:
          const factoryFunction = module[config.factory!];
          if (!factoryFunction || typeof factoryFunction !== "function") {
            throw new Error(
              `Service ${config.name}: Factory function ${config.factory} not found or not a function`,
            );
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return (factoryFunction as any)(...resolvedDependencies);

        case ServiceType.STATIC:
          // Static services don't need instantiation, just return the class
          return module[config.class!] || module.default;

        default:
          throw new Error(
            `Service ${config.name}: Unknown service type ${config.type}`,
          );
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(
          `Failed to load service ${config.name}: ${error.message}`,
        );
      }
      throw error;
    }
  }

  /**
   * Validate that a loaded service instance satisfies its declared interface contract.
   * Called by ServiceRegistry after each service is instantiated.
   */
  validateContract(config: ServiceConfig, instance: unknown): void {
    if (config.interface) {
      validateInterfaceContract(config.name, config.interface, instance);
    }
  }
}
